import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { MONTH_NAMES, formatDate, formatMoney, formatNumber } from '../lib/format';
import { FormPage } from '../components/FormPage';
import { Detail, Field, Input, Textarea, useToast } from '../components/ui';
import type { PointsReward } from './PointsRewards';

/** A refusal from the API, in the reader's language where there is a sentence for it. */
function refusal(e: unknown, t: (key: TranslationKey) => string): string {
  if (e instanceof ApiError && ['REDEMPTION_ALREADY_PROCESSED', 'PAYROLL_PERIOD_LOCKED', 'CONCURRENT_CHANGE'].includes(e.code)) {
    return t(`pointsRewards.error.${e.code}` as TranslationKey);
  }
  return e instanceof Error ? e.message : String(e);
}

/** What the reward is: whose, how much, when — the same on both pages. */
function Summary({ reward }: { reward: PointsReward }) {
  const { t, locale, localized } = useI18n();
  return (
    <dl className="grid gap-x-8 sm:grid-cols-2">
      <Detail label={t('pointsRewards.employee')}>{localized(reward.employee)}</Detail>
      <Detail label={t('pointsRewards.amount')}>
        <span className="tabular font-semibold">{formatMoney(reward.amount, reward.currency, locale)}</span>
      </Detail>
      <Detail label={t('pointsRewards.points')}>
        <span className="tabular">{formatNumber(reward.redemption.pointsSpent, locale)}</span>
      </Detail>
      <Detail label={t('pointsRewards.redeemed')}>
        <span className="tabular">{formatDate(reward.createdAt, locale)}</span>
      </Detail>
    </dl>
  );
}

function useReward(id: string) {
  return useQuery({ queryKey: ['points-rewards', 'one', id], queryFn: () => api.get<PointsReward>(`/wallet/cash-rewards/${id}`) });
}

/**
 * `/points-rewards/:id/mark-paid` — paid outside payroll, once the money went out:
 * in cash, by transfer. The employee then sees it paid.
 */
export function PointsRewardPaidPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reward = useReward(id);
  const [reference, setReference] = useState('');

  const save = useMutation({
    mutationFn: () => api.post(`/wallet/cash-rewards/${id}/mark-paid`, { reference: reference.trim() || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['points-rewards'] });
      toast.success(t('pointsRewards.paidDone'));
      navigate('/points-rewards');
    },
    onError: (e) => toast.error(refusal(e, t)),
  });

  return (
    <FormPage
      title={t('pointsRewards.paidTitle')}
      subtitle={t('pointsRewards.paidHint')}
      backTo="/points-rewards"
      backLabel={t('pointsRewards.title')}
      submitLabel={t('pointsRewards.markPaid')}
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!reward.data?.can.markPaid}
      loading={reward.isLoading}
      error={reward.error}
      onRetry={() => void reward.refetch()}
      summary={reward.data && <Summary reward={reward.data} />}
    >
      <Field label={t('pointsRewards.referenceLabel')} hint={t('pointsRewards.referenceHint')}>
        <Input value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} dir="auto" autoFocus />
      </Field>
    </FormPage>
  );
}

/**
 * `/points-rewards/:id/cancel` — cancelled, its points back in the wallet, once.
 * On a payroll not approved yet, it comes off that payslip. The reason stays in
 * Finance: the employee is told it was cancelled, not why.
 */
export function PointsRewardCancelPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reward = useReward(id);
  const [reason, setReason] = useState('');
  const r = reward.data;

  const save = useMutation({
    mutationFn: () => api.post(`/wallet/cash-rewards/${id}/cancel`, { reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['points-rewards'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      toast.success(t('pointsRewards.cancelDone'));
      navigate('/points-rewards');
    },
    onError: (e) => toast.error(refusal(e, t)),
  });

  return (
    <FormPage
      title={t('pointsRewards.cancelTitle')}
      subtitle={t('pointsRewards.cancelHint')}
      backTo="/points-rewards"
      backLabel={t('pointsRewards.title')}
      submitLabel={r ? t('pointsRewards.cancelSubmit', { points: formatNumber(r.redemption.pointsSpent, locale) }) : t('pointsRewards.cancel')}
      destructive
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!r?.can.cancel || !reason.trim()}
      loading={reward.isLoading}
      error={reward.error}
      onRetry={() => void reward.refetch()}
      summary={r && <Summary reward={r} />}
    >
      <div className="space-y-4">
        {r?.payroll && (
          <p className="rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-body-sm text-warning-fg dark:bg-warning/14 dark:text-warning-dark">
            {t('pointsRewards.cancelOnPayroll', {
              month: `${MONTH_NAMES[locale][r.payroll.month - 1]} ${r.payroll.year}`,
              amount: formatMoney(r.amount, r.currency, locale),
            })}
          </p>
        )}
        <Field label={t('pointsRewards.reasonLabel')} hint={t('pointsRewards.reasonHint')} required>
          <Textarea value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} dir="auto" autoFocus />
        </Field>
      </div>
    </FormPage>
  );
}
